import React, { useEffect, useState, useCallback } from 'react';
import { Box, Typography, CircularProgress, Button } from '@mui/material';
import BuildIcon from '@mui/icons-material/Build';
import RefreshIcon from '@mui/icons-material/Refresh';
import { bffConfigService } from '@resort-os/api-client';
import { useAuth } from '../contexts/AuthContext';

const ACCENT = '#2C3E2D';
const BORDER = '1px solid #E2DDD8';

interface Props {
  children: React.ReactNode;
}

export default function MaintenanceGuard({ children }: Props) {
  const { user, isLoading: authLoading, isAuthenticated } = useAuth();
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(false);

  const isAdminOrManager = user?.role === 'admin' || user?.role === 'manager';

  const checkMaintenance = useCallback(async () => {
    if (!isAuthenticated) {
      setLoading(false);
      setMaintenanceMode(false);
      return;
    }
    try {
      const res = await bffConfigService.getAll();
      const configs = res.configs || [];
      const modeConfig = configs.find((c: any) => c.key === 'system.maintenance_mode');
      const enabled = modeConfig
        ? typeof modeConfig.value === 'boolean'
          ? modeConfig.value
          : String(modeConfig.value) === 'true'
        : false;
      setMaintenanceMode(enabled);

      if (enabled) {
        const msgConfig = configs.find((c: any) => c.key === 'system.maintenance_message');
        setMaintenanceMessage(typeof msgConfig?.value === 'string' ? msgConfig.value : '');
      } else {
        setMaintenanceMessage('');
      }
    } catch (err: any) {
      if (err?.response?.status === 503) {
        setMaintenanceMode(true);
      }
    } finally {
      setLoading(false);
      setRetrying(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    checkMaintenance();
  }, [checkMaintenance]);

  useEffect(() => {
    if (!maintenanceMode || isAdminOrManager) return;
    const interval = setInterval(checkMaintenance, 30000);
    return () => clearInterval(interval);
  }, [maintenanceMode, isAdminOrManager, checkMaintenance]);

  const handleRetry = () => {
    setRetrying(true);
    checkMaintenance();
  };

  if (loading || authLoading) return null;

  if (maintenanceMode && !isAdminOrManager) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="#FAFAF8"
      >
        <Box sx={{ width: 440, border: BORDER, bgcolor: '#FFFFFF', p: 5, textAlign: 'center' }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              border: BORDER,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3,
            }}
          >
            <BuildIcon sx={{ fontSize: 28, color: ACCENT }} />
          </Box>
          <Typography
            sx={{
              fontSize: '1.25rem',
              fontWeight: 600,
              color: '#1A1A1A',
              mb: 1,
            }}
          >
            Under Maintenance
          </Typography>
          <Typography sx={{ color: '#6B6560', fontSize: '0.85rem', lineHeight: 1.6, mb: 3 }}>
            {maintenanceMessage ||
              "The system is currently undergoing scheduled maintenance. We'll be back shortly."}
          </Typography>
          <Button
            variant="outlined"
            onClick={handleRetry}
            disabled={retrying}
            startIcon={
              retrying ? <CircularProgress size={14} /> : <RefreshIcon sx={{ fontSize: 16 }} />
            }
            sx={{
              borderRadius: 0,
              borderColor: ACCENT,
              color: ACCENT,

              fontSize: '0.75rem',
            }}
          >
            {retrying ? 'Checking...' : 'Retry'}
          </Button>
        </Box>
      </Box>
    );
  }

  if (maintenanceMode && isAdminOrManager) {
    return (
      <Box>
        <Box
          sx={{
            px: 3,
            py: 1.5,
            bgcolor: '#FFF3E0',
            borderBottom: '1px solid #FFE0B2',
            textAlign: 'center',
          }}
        >
          <Typography
            sx={{
              fontSize: '0.75rem',
              color: '#E65100',

              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
            }}
          >
            <BuildIcon sx={{ fontSize: 14 }} />
            Maintenance mode is active — regular users cannot access the system
          </Typography>
        </Box>
        {children}
      </Box>
    );
  }

  return <>{children}</>;
}
